import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Info, Navigation,
  Wifi, MapPin, Heart, Share2, 
  ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { Card } from '../components/ui/card';
import ContactSection from '../components/property/ContactSection';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/toast/use-toast';
import MainPropertySection from '../components/property/MainPropertySection';
import CharacteristicsSection from '../components/property/CharacteristicsSection';
import LocationSection from '../components/property/LocationSection';
import { useVisitTracker } from '../hooks/useVisitTracker';
import { ScrollRevealSection, ScrollReveal } from '../components/property/ScrollReveal';
import ScrollButton from '../components/property/ScrollButton';

// Custom hook for scrolling
const useScrollObserver = () => {
  const [scrollDirection, setScrollDirection] = useState('down');
  const [scrollProgress, setScrollProgress] = useState(0);
  const prevScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine scroll direction
      if (currentScrollY > prevScrollY.current) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }
      
      // Calculate scroll progress (0-100%)
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(100, Math.max(0, (currentScrollY / scrollHeight) * 100));
      setScrollProgress(progress);
      
      prevScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { scrollDirection, scrollProgress };
};

const AlquilerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [alquiler, setAlquiler] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
  const [showAllCharacteristics, setShowAllCharacteristics] = useState(false);
  const [isShowingFlyer, setIsShowingFlyer] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Usar el hook de seguimiento de visitas
  // No intentará hacer llamadas API para alquileres
  const { elapsedTime } = useVisitTracker(id);

  // Custom hook para scroll
  const { scrollDirection, scrollProgress } = useScrollObserver();

  useEffect(() => {
    const fetchAlquilerDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        console.log('Obteniendo alquiler:', id);
        const response = await fetch(`https://backend-production-1786.up.railway.app/api/alquiler/${id}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Accept': 'application/json'
          }
        });
    
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Error al cargar el alquiler');
        }
    
        const data = await response.json();
    
        // Asegurarse de que los datos estén correctamente formateados
        const formattedData = {
          ...data,
          precio_soles: data.precio_mensual,
          precio_tipo: '/mes',
          operacion: 'alquiler',
          tipo_operacion: 'alquiler',
          vendedor: {
            ...data.vendedor,
            email_verificado: Boolean(data.vendedor?.email_verificado),
            telefono_verificado: Boolean(data.vendedor?.telefono_verificado)
          }
        };
    
        setAlquiler(formattedData);
      } catch (error) {
        console.error('Error detallado:', error);
        toast({
          title: "Error",
          description: error.message || "No se pudo cargar el alquiler",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAlquilerDetails();
  }, [id, toast]);

  // Verificar estado de favoritos cuando cambia el usuario
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!user) return;

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`https://backend-production-1786.up.railway.app/api/favoritos/check/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const { isFavorite: favoriteStatus } = await response.json();
          setIsFavorite(favoriteStatus);
        }
      } catch (error) {
        console.error('Error verificando estado de favoritos:', error);
      }
    };

    checkFavoriteStatus();
  }, [id, user]);

  // Funciones auxiliares - optimizadas con useCallback
  const formatPrice = useCallback((price) => {
    return new Intl.NumberFormat('es-PE').format(price);
  }, []);

  const handleNextImage = useCallback(() => {
    if (!alquiler?.imagenes?.length) return;
    setCurrentImageIndex((prev) => 
      prev === alquiler.imagenes.length - 1 ? 0 : prev + 1
    );
  }, [alquiler?.imagenes?.length]);

  const handlePrevImage = useCallback(() => {
    if (!alquiler?.imagenes?.length) return;
    setCurrentImageIndex((prev) => 
      prev === 0 ? alquiler.imagenes.length - 1 : prev - 1
    );
  }, [alquiler?.imagenes?.length]);

  const handleShare = useCallback(async () => {
    if (!alquiler) return;
    
    try {
      await navigator.share({
        title: alquiler.titulo,
        text: `Mira este alquiler en InKayros: ${alquiler.titulo}`,
        url: window.location.href
      });
    } catch (error) {
      console.log('Error compartiendo:', error);
      if (error.name === 'NotSupportedError') {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: "Link copiado",
          description: "El enlace se ha copiado al portapapeles",
        });
      } else {
        toast({
          title: "Error",
          description: "No se pudo compartir el alquiler",
          variant: "destructive",
        });
      }
    }
  }, [alquiler, toast]);

  const handleFavorite = useCallback(async () => {
    if (!user) {
      toast({
        title: "Inicia sesión para guardar favoritos",
        description: "Necesitas una cuenta para guardar alquileres en favoritos",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    setIsLoadingFavorite(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No hay token disponible');

      const response = await fetch(`https://backend-production-1786.up.railway.app/api/favoritos/${id}`, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al actualizar favoritos');
      }

      const newFavoriteStatus = !isFavorite;
      setIsFavorite(newFavoriteStatus);
      
      toast({
        title: newFavoriteStatus ? "Añadido a favoritos" : "Eliminado de favoritos",
        description: newFavoriteStatus 
          ? "El alquiler se añadió a tus favoritos" 
          : "El alquiler se eliminó de tus favoritos",
      });

    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar favoritos",
        variant: "destructive",
      });
    } finally {
      setIsLoadingFavorite(false);
    }
  }, [id, isFavorite, navigate, toast, user]);

  const scrollToContact = useCallback(() => {
    const section = document.getElementById('contacto');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  if (loading || !alquiler) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 via-blue-100 to-pink-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex justify-center items-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-400"></div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">Cargando alquiler...</p>
        </div>
      </div>
    );
  }

  // Prepare property data object once - not inside render method
  const propertyData = {
    ...alquiler,
    id: alquiler.id,
    precio_soles: alquiler.precio_mensual,
    precio_tipo: '/mes',
    mantenimiento: alquiler.mantenimiento || 0,
    vendedor: {
      ...alquiler.vendedor,
      foto_perfil: alquiler.vendedor.foto_perfil,
      calificacion_promedio: parseFloat(alquiler.vendedor.calificacion_promedio || 0).toFixed(1),
      email_verificado: Boolean(alquiler.vendedor.email_verificado),
      telefono_verificado: Boolean(alquiler.vendedor.telefono_verificado)
    },
    operacion: 'alquiler',
    tipo_operacion: 'alquiler'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-100 via-blue-100 to-pink-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50">
        <div 
          className="h-full bg-gradient-to-r from-blue-400 to-blue-600 dark:from-blue-500 dark:to-blue-700 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      
      {/* Scroll Button */}
      <ScrollButton />

      <div className="relative">
        {/* Main Section */}
        <div id="main">
          <MainPropertySection 
            property={propertyData}
            formatPrice={formatPrice}
            handleContact={scrollToContact}
            currentImageIndex={currentImageIndex}
            handleFavorite={handleFavorite}
            handleShare={handleShare}
            handlePrevImage={handlePrevImage}
            handleNextImage={handleNextImage}
            setCurrentImageIndex={setCurrentImageIndex}
            isFavorite={isFavorite}
            showFlyer={() => setIsShowingFlyer(true)}
            isRental={true}
          />
        </div>

        {/* About Section */}
        <div id="sobre" className="min-h-[70vh] sm:min-h-[80vh] flex items-center justify-center py-8 sm:py-16">
          <ScrollRevealSection
            className="w-full"
            direction="up"
            distance={30}
            duration={0.6}
          >
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 w-full">
              <Card className="backdrop-blur-xl bg-white/80 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-4 sm:p-6 lg:p-8 mx-2 sm:mx-0 transform transition-all duration-500 hover:shadow-2xl dark:hover:shadow-black/50 overflow-hidden">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-50 dark:bg-blue-500/20 p-2 rounded-xl">
                    <Info className="w-6 h-6 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
                    Sobre el Inmueble
                  </h2>
                </div>
                
                {/* Container con mejor manejo del texto */}
                <div className="space-y-4">
                  <div className="prose prose-sm sm:prose-lg max-w-none">
                    <div className="text-gray-700 dark:text-gray-300 leading-relaxed break-words hyphens-auto text-sm sm:text-base lg:text-lg whitespace-pre-line">
                      {showFullDescription 
                        ? (propertyData.descripcion_corta || 'No hay descripción disponible')
                        : (propertyData.descripcion_corta && propertyData.descripcion_corta.length > 300 
                          ? `${propertyData.descripcion_corta.substring(0, 300)}...`
                          : (propertyData.descripcion_corta || 'No hay descripción disponible')
                        )
                      }
                    </div>
                  </div>
                  
                  {/* Si la descripción es muy larga, agregar opción de expandir */}
                  {propertyData.descripcion_corta && propertyData.descripcion_corta.length > 300 && (
                    <div className="mt-4">
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base"
                      >
                        {showFullDescription ? 'Ver menos' : 'Ver más'}
                        <ChevronRight className={`w-4 h-4 transform transition-transform duration-200 ${showFullDescription ? 'rotate-90' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="mt-6 sm:mt-8 pt-4 border-t border-gray-100 dark:border-white/10">
                  <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"></div>
                    <span>Información actualizada</span>
                  </div>
                </div>
              </Card>
            </div>
          </ScrollRevealSection>
        </div>

        {/* Characteristics Section */}
        <div id="caracteristicas" className="min-h-[90vh] flex items-center justify-center py-8 sm:py-16">
          <div className="w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <CharacteristicsSection 
                property={propertyData}
                showAll={showAllCharacteristics}
                toggleShowAll={() => setShowAllCharacteristics(!showAllCharacteristics)}
                isRental={true}
              />
            </div>
          </div>
        </div>

        {/* Location Section */}
        <div id="ubicacion" className="min-h-screen flex items-center justify-center py-8 sm:py-16">
          <ScrollRevealSection
            className="w-full"
            direction="right"
            distance={50}
            duration={0.7}
          >
            <LocationSection property={propertyData} />
          </ScrollRevealSection>
        </div>

        {/* Contact Section */}
        <div id="contacto" className="min-h-screen flex items-center justify-center py-8 sm:py-16">
          <ScrollRevealSection
            className="w-full"
            direction="up"
            distance={50}
            duration={0.7}
          >
            <ContactSection 
              property={propertyData} 
              handleContact={scrollToContact}
              isRental={true}
            />
          </ScrollRevealSection>
        </div>
      </div>
    </div>
  );
};

export default AlquilerDetail;